import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "i18n";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Card } from "reactstrap";
import BtnCustom from "components/common/btn-custom";
import { ErrorToaster, successToaster } from "components/common/toast";
import handleError from "utils/handleError";
import {
  BeverageAIDraft,
  CreateBeverage,
  GetBeverageSchema,
  GetSingleBeverage,
  UpdateBeverage,
} from "network/beverages";
import {
  ALLERGENS,
  BEVERAGE_IMAGE_FIELDS,
  BEVERAGE_SECTIONS,
  sectionLabel,
} from "constant/beverages";

const BASE_URL = process.env.REACT_APP_BASE_URL || "";

const emptyBase = { name: "", ean: "", video_url: "" };

// Un campo dello schema (testo, testo lungo, numero, sì/no): il tipo lo decide il backend.
function SchemaField({ field, value, onChange, suggested }) {
  const { t } = useTranslation("beverages");
  const label = field?.label?.[i18n.language] || field?.label?.it || field.key;
  const id = `bev-${field.key}`;
  let input;
  if (field.type === "long") {
    input = (
      <textarea
        id={id}
        className="form-control"
        rows={3}
        value={value ?? ""}
        onChange={(e) => onChange(field.key, e.target.value)}
      />
    );
  } else if (field.type === "bool") {
    input = (
      <select
        id={id}
        className="form-control"
        value={value ?? ""}
        onChange={(e) => onChange(field.key, e.target.value)}
      >
        <option value="">-</option>
        <option value="true">{t("form.yes")}</option>
        <option value="false">{t("form.no")}</option>
      </select>
    );
  } else {
    input = (
      <input
        id={id}
        className="form-control"
        type="text"
        inputMode={field.type === "number" ? "decimal" : "text"}
        value={value ?? ""}
        onChange={(e) => onChange(field.key, e.target.value)}
      />
    );
  }
  return (
    <div
      className={`mb-3 ${
        field.type === "long" ? "col-12 col-md-6" : "col-12 col-sm-6 col-lg-3"
      }`}
    >
      <label htmlFor={id}>
        {label}
        {suggested && <span className="text-warning">{t("form.labelFallbackSuggested")}</span>}
      </label>
      {input}
    </div>
  );
}

export default function BeverageForm() {
  const { t } = useTranslation("beverages");
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [section, setSection] = useState(
    BEVERAGE_SECTIONS.some((s) => s.code === searchParams.get("section"))
      ? searchParams.get("section")
      : "beverages"
  );
  const [schema, setSchema] = useState(null);
  const [base, setBase] = useState(emptyBase);
  const [values, setValues] = useState({});
  const [allergens, setAllergens] = useState([]);
  const [files, setFiles] = useState({});
  const [currentImages, setCurrentImages] = useState({});
  const [flags, setFlags] = useState({ is_active: true, is_approved: true });
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  // Bozza AI (solo in inserimento)
  const [aiFiles, setAiFiles] = useState([]);
  const [aiText, setAiText] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [suggested, setSuggested] = useState([]);
  const [duplicates, setDuplicates] = useState([]);

  const setValue = (key, value) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  // In modifica la categoria viene dalla scheda, in inserimento dal selettore.
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const res = await GetSingleBeverage(id);
        const d = res?.data || {};
        setSection(d.section);
        setBase({
          name: d.name || "",
          ean: d.ean || "",
          video_url: d.video_url || "",
        });
        const flat = {};
        ["brand", "country", "region", "volume_cl", "abv", "ingredients", "pairing", "kcal", "description"].forEach(
          (k) => {
            if (d[k] !== null && d[k] !== undefined) flat[k] = String(d[k]);
          }
        );
        Object.entries(d.nutrition || {}).forEach(([k, v]) => {
          flat[k] = v === null || v === undefined ? "" : String(v);
        });
        Object.entries(d.details || {}).forEach(([k, v]) => {
          flat[k] = v === null || v === undefined ? "" : String(v);
        });
        setValues(flat);
        setAllergens(d.allergens || []);
        setCurrentImages(d.images || {});
        setFlags({
          is_active: d.is_active !== false,
          is_approved: d.is_approved !== false,
        });
      } catch (error) {
        ErrorToaster(handleError(error?.status_code, error?.message));
        navigate("/beverages/view");
      }
      setLoading(false);
    })();
  }, [id]);

  useEffect(() => {
    setSchema(null);
    (async () => {
      try {
        const res = await GetBeverageSchema(section);
        setSchema(res?.data || null);
      } catch (error) {
        ErrorToaster(handleError(error?.status_code, error?.message));
      }
    })();
  }, [section]);

  const groups = useMemo(
    () =>
      schema
        ? [
            { title: t("form.groupGeneral"), fields: schema.columns },
            { title: t("form.groupNutrition"), fields: schema.nutrition },
            { title: t("form.groupDetails", { section: sectionLabel(section) }), fields: schema.details },
          ]
        : [],
    [schema, section, t]
  );

  const toggleAllergen = (code) =>
    setAllergens((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );

  const readDraft = async () => {
    if (!aiFiles.length && !aiText.trim()) {
      ErrorToaster(t("form.aiNeedInput"));
      return;
    }
    const form = new FormData();
    form.append("section", section);
    aiFiles.forEach((f) => form.append("images", f));
    if (aiText.trim()) form.append("text", aiText.trim());
    if (base.ean) form.append("ean", base.ean);
    try {
      setAiBusy(true);
      const res = await BeverageAIDraft(form);
      const draft = res?.data?.draft || {};
      setBase((prev) => ({
        name: draft.name || prev.name,
        ean: draft.ean || prev.ean,
        video_url: prev.video_url,
      }));
      const flat = { ...values };
      Object.entries(draft).forEach(([k, v]) => {
        if (["nutrition", "details", "suggested_fields", "allergens", "name", "ean"].includes(k)) return;
        if (v !== null && v !== undefined) flat[k] = String(v);
      });
      Object.entries(draft.nutrition || {}).forEach(([k, v]) => (flat[k] = String(v)));
      Object.entries(draft.details || {}).forEach(([k, v]) => (flat[k] = String(v)));
      setValues(flat);
      if (Array.isArray(draft.allergens)) setAllergens(draft.allergens);
      setSuggested(draft.suggested_fields || []);
      setDuplicates(res?.data?.duplicates || []);
      successToaster(t("form.aiReady"));
    } catch (error) {
      ErrorToaster(handleError(error?.status_code, error?.message));
    }
    setAiBusy(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!base.name.trim()) {
      ErrorToaster(t("form.nameRequired"));
      return;
    }
    const form = new FormData();
    if (!isEdit) form.append("section", section);
    form.append("name", base.name.trim());
    if (isEdit || base.ean) form.append("ean", base.ean);
    if (isEdit || base.video_url) form.append("video_url", base.video_url);
    if (isEdit || allergens.length) form.append("allergens", allergens.join(","));
    (schema ? groups.flatMap((g) => g.fields) : []).forEach((f) => {
      const v = values[f.key] ?? "";
      // In inserimento si inviano solo i campi compilati; in modifica anche i vuoti, per poterli svuotare.
      if (isEdit || v !== "") form.append(f.key, v);
    });
    if (isEdit) {
      form.append("is_active", String(flags.is_active));
      form.append("is_approved", String(flags.is_approved));
    }
    Object.entries(files).forEach(([field, file]) => {
      if (file) form.append(field, file);
    });
    try {
      setSaving(true);
      const res = isEdit
        ? await UpdateBeverage(id, form)
        : await CreateBeverage(form);
      if (res.success) {
        successToaster(isEdit ? t("form.updated") : t("form.created"));
        navigate("/beverages/view");
      }
    } catch (error) {
      ErrorToaster(handleError(error?.status_code, error?.message));
    }
    setSaving(false);
  };

  if (loading) return <div className="content my-4">{t("form.loading")}</div>;

  return (
    <div className="content my-4">
      <Card className="p-4">
        <h2 className="card-title">
          {isEdit ? t("form.editTitle") : t("form.newTitle")} · {sectionLabel(section)}
        </h2>

        {!isEdit && (
          <div className="d-flex flex-wrap mb-3">
            {BEVERAGE_SECTIONS.map((s) => (
              <button
                key={s.code}
                type="button"
                className={`btn btn-sm mr-2 ${
                  section === s.code ? "btn-primary" : "btn-secondary"
                }`}
                onClick={() => setSection(s.code)}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}

        {!isEdit && (
          <div className="border rounded p-3 mb-4">
            <h4 className="mb-2">{t("form.aiTitle")}</h4>
            <p className="text-muted mb-2">
              {t("form.aiIntro")}
            </p>
            <div className="row">
              <div className="col-12 col-md-6 mb-2">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  onChange={(e) => setAiFiles(Array.from(e.target.files || []))}
                />
              </div>
              <div className="col-12 col-md-6 mb-2">
                <input
                  className="form-control"
                  placeholder={t("form.aiTextPlaceholder")}
                  value={aiText}
                  onChange={(e) => setAiText(e.target.value)}
                />
              </div>
            </div>
            <BtnCustom
              type="button"
              color="btn btn-secondary text-white"
              isLoading={aiBusy}
              submittingText={t("form.aiReading")}
              onClick={readDraft}
            >
              {t("form.aiRead")}
            </BtnCustom>
            {duplicates.length > 0 && (
              <div className="text-warning mt-3">
                {t("form.duplicates")}{" "}
                {duplicates
                  .map((d) => `${d.name}${d.brand ? ` (${d.brand})` : ""}`)
                  .join(", ")}
                .
              </div>
            )}
          </div>
        )}

        <form onSubmit={submit}>
          <div className="row">
            <div className="col-12 col-sm-6 col-lg-4 mb-3">
              <label htmlFor="bev-name">{t("form.name")}</label>
              <input
                id="bev-name"
                className="form-control"
                value={base.name}
                onChange={(e) => setBase({ ...base, name: e.target.value })}
              />
            </div>
            <div className="col-12 col-sm-6 col-lg-4 mb-3">
              <label htmlFor="bev-ean">{t("form.ean")}</label>
              <input
                id="bev-ean"
                className="form-control"
                inputMode="numeric"
                value={base.ean}
                onChange={(e) => setBase({ ...base, ean: e.target.value })}
              />
            </div>
            <div className="col-12 col-sm-6 col-lg-4 mb-3">
              <label htmlFor="bev-video">{t("form.video")}</label>
              <input
                id="bev-video"
                className="form-control"
                value={base.video_url}
                onChange={(e) => setBase({ ...base, video_url: e.target.value })}
              />
            </div>
          </div>

          {!schema && <p className="text-muted">{t("form.loadingFields")}</p>}
          {groups.map((group) => (
            <div key={group.title} className="mb-3">
              <h4 className="border-top border-light pt-3">{group.title}</h4>
              <div className="row">
                {group.fields.map((f) => (
                  <SchemaField
                    key={f.key}
                    field={f}
                    value={values[f.key]}
                    onChange={setValue}
                    suggested={suggested.includes(f.key)}
                  />
                ))}
              </div>
            </div>
          ))}

          <h4 className="border-top border-light pt-3">{t("form.allergens")}</h4>
          <div className="d-flex flex-wrap mb-3">
            {ALLERGENS.map((a) => (
              <label key={a.code} className="mr-4 mb-2">
                <input
                  type="checkbox"
                  className="mr-1"
                  checked={allergens.includes(a.code)}
                  onChange={() => toggleAllergen(a.code)}
                />
                {a.label}
              </label>
            ))}
          </div>

          <h4 className="border-top border-light pt-3">{t("form.photos")}</h4>
          <div className="row mb-3">
            {BEVERAGE_IMAGE_FIELDS.map((f) => (
              <div key={f.name} className="col-12 col-sm-6 col-lg-4 mb-3">
                <label>{f.label}</label>
                <input
                  type="file"
                  className="d-block"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(e) =>
                    setFiles({ ...files, [f.name]: e.target.files?.[0] || null })
                  }
                />
                {currentImages[f.name] && (
                  <a
                    href={`${BASE_URL}${
                      String(currentImages[f.name]).startsWith("/") ? "" : "/"
                    }${currentImages[f.name]}`}
                    target="_blank"
                    rel="noreferrer"
                    className="small"
                  >
                    {t("form.currentImage")}
                  </a>
                )}
              </div>
            ))}
          </div>

          {isEdit && (
            <div className="row mb-3">
              <div className="col-12 col-sm-6 col-lg-3">
                <label>{t("form.active")}</label>
                <select
                  className="form-control"
                  value={String(flags.is_active)}
                  onChange={(e) =>
                    setFlags({ ...flags, is_active: e.target.value === "true" })
                  }
                >
                  <option value="true">{t("form.yes")}</option>
                  <option value="false">{t("form.no")}</option>
                </select>
              </div>
              <div className="col-12 col-sm-6 col-lg-3">
                <label>{t("form.approved")}</label>
                <select
                  className="form-control"
                  value={String(flags.is_approved)}
                  onChange={(e) =>
                    setFlags({ ...flags, is_approved: e.target.value === "true" })
                  }
                >
                  <option value="true">{t("form.yes")}</option>
                  <option value="false">{t("form.no")}</option>
                </select>
              </div>
            </div>
          )}

          <BtnCustom
            isLoading={saving}
            type="submit"
            color="btn btn-primary mt-3 mr-3"
            submittingText={t("form.saving")}
          >
            {isEdit ? t("form.saveChanges") : t("form.create")}
          </BtnCustom>
          <BtnCustom
            type="button"
            disabled={saving}
            color="btn btn-secondary mt-3 text-white"
            onClick={() => navigate("/beverages/view")}
          >
            {t("form.cancel")}
          </BtnCustom>
        </form>
      </Card>
    </div>
  );
}
